import type { SsoSilentRequest } from "@azure/msal-browser";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { SuccessResponse } from "@/types/api";
import {
	clearAppInsightsUser,
	setAppInsightsUser,
} from "../../../config/appInsights.ts";
import {
	apiScopes,
	msalInstance,
	recoverExpiredMsalSession,
	tryPromptNoneReauth,
} from "../../../config/authConfig";
import apiClient from "../../../config/axiosConfig";
import { authPersistence } from "../../../utils/authPersistence";
import {
	clearTokenRefresh,
	setupTokenRefresh,
} from "../../../utils/tokenRefresh";

const waitForRedirectNavigation = () => new Promise<never>(() => {});

export type Role = string;

export interface User {
	id: string;
	email: string;
	name: string;
	roles: Role[]; // Update roles to be an array of Role objects
}

export interface AuthState {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: null | User;
}

export const initializeAuth = createAsyncThunk(
	"auth/initializeAuth",
	async () => {
		// First check if we have an active account (from MSAL initialization)
		let account = msalInstance.getActiveAccount();

		// If no active account, check all accounts
		if (!account) {
			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				account = accounts[0];
				msalInstance.setActiveAccount(account);
			}
		}

		// Try silent SSO even if we don't yet have an active account
		const savedUsernameSymbol = Symbol.for("__msal_saved_username");
		const savedUsername = (
			window as unknown as Record<string, string | undefined>
		)[savedUsernameSymbol as unknown as string] as string | undefined;
		if (!account) {
			try {
				const ssoParams: SsoSilentRequest = savedUsername
					? {
							scopes: apiScopes,
							loginHint: savedUsername,
						}
					: {
							scopes: apiScopes,
						};

				const tokenResponse = await msalInstance.ssoSilent(ssoParams);

				if (tokenResponse.account) {
					account = tokenResponse.account;
					msalInstance.setActiveAccount(account);
					authPersistence.saveAccount(account);
				}
			} catch (silentError: unknown) {
				if (
					(await recoverExpiredMsalSession(silentError)) &&
					(await tryPromptNoneReauth(savedUsername))
				) {
					return await waitForRedirectNavigation();
				}

				// Silent login failed, user will need to login interactively
				console.debug(
					"[Auth] Silent SSO failed, user needs to login interactively",
				);
			} finally {
				// Clean up the saved username from window object
				if (savedUsername) {
					delete (window as unknown as Record<string, unknown>)[
						savedUsernameSymbol as unknown as string
					];
				}
			}
		}

		if (account) {
			try {
				// Try to acquire token silently
				const tokenResponse = await msalInstance.acquireTokenSilent({
					scopes: apiScopes,
					account,
					forceRefresh: false,
				});

				// Set the authorization header
				apiClient.defaults.headers.common.Authorization = `Bearer ${tokenResponse.accessToken}`;

				// Fetch user roles from the backend
				const rolesResponse =
					await apiClient.get<SuccessResponse<{ roles: Role[] }>>(
						"/users/roles",
					);
				if (!rolesResponse.data.data) {
					throw new Error("No roles found");
				}
				const roles = rolesResponse.data.data.roles;

				return {
					account: account,
					token: tokenResponse,
					roles: roles, // Include roles in the returned payload
				};
			} catch (error: unknown) {
				if (await recoverExpiredMsalSession(error)) {
					delete apiClient.defaults.headers.common.Authorization;
					if (await tryPromptNoneReauth(account.username || savedUsername)) {
						return await waitForRedirectNavigation();
					}

					throw new Error("Interactive login required");
				}
				// Log only non-sensitive error information
				console.error("[Auth] Token acquisition failed");
				throw error;
			}
		} else {
			// No account found - user needs to login
			throw new Error("No active account found.");
		}
	},
);

// Add a logout thunk that properly handles MSAL logout
export const logoutUser = createAsyncThunk("auth/logout", async () => {
	try {
		// Clear token refresh timer
		clearTokenRefresh();
		clearAppInsightsUser();

		// Set a flag to indicate logout is in progress
		sessionStorage.setItem("msal_logout_in_progress", "true");

		// Clear the authorization header
		delete apiClient.defaults.headers.common.Authorization;

		// Clear our custom persistence
		authPersistence.clearAccount();

		// Clear all accounts from MSAL cache
		const accounts = msalInstance.getAllAccounts();
		if (accounts.length > 0) {
			// Clear active account
			msalInstance.setActiveAccount(null);
		}

		// Clear localStorage but preserve the logout flag in sessionStorage
		localStorage.clear();

		// Logout from MSAL - this will clear server-side session
		await msalInstance.logoutRedirect({
			postLogoutRedirectUri: `${window.location.origin}/login`,
			account: accounts[0], // Specify which account to logout
		});
	} catch (error) {
		console.error("Logout failed:", error);
		// Even if logout fails, clear local state
		sessionStorage.setItem("msal_logout_in_progress", "true");
		localStorage.clear();
		authPersistence.clearAccount();
		// Force redirect to login
		window.location.href = "/login";
	}
});

const authSlice = createSlice({
	name: "auth",
	initialState: {
		isAuthenticated: false,
		isLoading: true,
		user: null,
	} as AuthState,
	reducers: {
		logout: (state) => {
			state.isAuthenticated = false;
			state.user = null;
			localStorage.clear(); // Clear all local storage
			clearTokenRefresh(); // Clear token refresh timer
			clearAppInsightsUser();
		},
		setLoadingComplete: (state) => {
			state.isLoading = false;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(initializeAuth.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(initializeAuth.fulfilled, (state, action) => {
			state.isAuthenticated = true;
			state.isLoading = false;
			state.user = {
				id: action.payload.account.localAccountId,
				email: action.payload.account.username,
				name: action.payload.account.name || "",
				roles: action.payload.roles, // Store roles in the state
			};
			// Set up proactive token refresh on successful authentication
			setupTokenRefresh();
			// Associate telemetry with the authenticated user
			setAppInsightsUser(
				action.payload.account.localAccountId,
				action.payload.account.tenantId,
				action.payload.account.username,
			);
		});
		builder.addCase(initializeAuth.rejected, (state) => {
			state.isAuthenticated = false;
			state.isLoading = false;
			state.user = null;
		});
	},
});

export const { logout, setLoadingComplete } = authSlice.actions;
export default authSlice.reducer;
